import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, Pencil, X, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/* ─── Categorias de custo (mesmas do Financeiro) ─── */
const CUSTOS = [
  { key: 'custo_equipamento',       pagoKey: 'custo_equipamento_pago_em',       label: 'Equipamento' },
  { key: 'custo_instalacao',        pagoKey: 'custo_instalacao_pago_em',        label: 'Instalação' },
  { key: 'custo_indicacao',         pagoKey: 'custo_indicacao_pago_em',         label: 'Indicação' },
  { key: 'custo_logistica',         pagoKey: 'custo_logistica_pago_em',         label: 'Logística' },
  { key: 'custo_material_eletrico', pagoKey: 'custo_material_eletrico_pago_em', label: 'Material Elétrico' },
  { key: 'custo_crt',               pagoKey: 'custo_crt_pago_em',               label: 'CRT' },
  { key: 'custo_padrao',            pagoKey: 'custo_padrao_pago_em',            label: 'Padrão' },
  { key: 'custo_brinde',            pagoKey: 'custo_brinde_pago_em',            label: 'Brinde' },
];

const fmt = (v) =>
  v != null && v !== 0
    ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
    : null;

/* Verifica se um financeiro tem todos os custos pagos */
const isTotalmentePago = (f) => {
  const custoComValor = CUSTOS.filter(({ key }) => parseFloat(f[key]) > 0);
  if (custoComValor.length === 0) return false;
  return custoComValor.every(({ pagoKey }) => !!f[pagoKey]);
};

const S = {
  heading: { fontSize: 22, fontWeight: 700, color: '#f6c90e', marginBottom: 4 },
  sub:     { fontSize: 13, color: '#475569', marginBottom: 20 },
  wrap:    { overflowX: 'auto', background: '#0e1420', border: '1px solid #1a2035', borderRadius: 12 },
  table:   { borderCollapse: 'collapse', minWidth: 600 },
  thRow:   { background: '#0b111e' },
  thLabel: { padding: '10px 16px', fontSize: 11, color: '#475569', textTransform: 'uppercase',
             letterSpacing: 1, borderBottom: '1px solid #1a2035', borderRight: '1px solid #1a2035',
             fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#0b111e', zIndex: 2 },
  thClient: { padding: '10px 14px', fontSize: 12, color: '#e2e8f0', fontWeight: 700,
              borderBottom: '1px solid #1a2035', borderRight: '1px solid #1a2035',
              whiteSpace: 'nowrap', textAlign: 'center', minWidth: 160 },
  tdLabel: { padding: '10px 16px', fontSize: 13, color: '#94a3b8', fontWeight: 600,
             borderBottom: '1px solid #0f1117', borderRight: '1px solid #1a2035',
             whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#0e1420', zIndex: 1 },
  tdTotal: { padding: '10px 16px', fontSize: 13, fontWeight: 700, color: '#f6c90e',
             borderBottom: '1px solid #1a2035', borderRight: '1px solid #1a2035',
             whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#0b111e', zIndex: 1 },
};

/* ─── Célula com edição inline (valor + data) ─── */
function Cell({ finId, valorKey, valor, pagoKey, pagoEm, onSaved, highlighted, extra }) {
  const [editing, setEditing]     = useState(false);
  const [dataDraft, setDataDraft] = useState(pagoEm || '');
  const [valorDraft, setValorDraft] = useState(valor != null ? String(valor) : '');
  const [saving, setSaving]       = useState(false);

  const pago = !!pagoEm;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/financeiro/${finId}/marcar-custo-pago/`, {
        campo:     pagoKey,
        data:      dataDraft || null,
        valor_key: valorKey,
        valor:     valorDraft !== '' ? valorDraft : null,
      });
      onSaved(finId, pagoKey, dataDraft || null, valorKey, valorDraft !== '' ? parseFloat(valorDraft.replace(',', '.')) : null);
      setEditing(false);
    } catch (e) {
      alert('Erro ao salvar: ' + (e.message || 'tente novamente'));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = () => {
    setDataDraft(pagoEm || '');
    setValorDraft(valor != null ? String(valor) : '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setDataDraft(pagoEm || '');
    setValorDraft(valor != null ? String(valor) : '');
    setEditing(false);
  };

  if (!valor && !editing) {
    return (
      <td style={{
        padding: '8px 10px', borderBottom: '1px solid #0f1117',
        borderRight: '1px solid #1a2035', textAlign: 'center',
        color: '#2d3748', fontSize: 12, minWidth: 150,
        background: highlighted ? 'rgba(246,201,14,0.04)' : '',
      }}>
        {extra && <div style={{ marginBottom: 6 }}>{extra}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
          <span>—</span>
          <button
            onClick={openEdit}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2d3748', padding: 2, display: 'flex', opacity: 0.5 }}
            title="Adicionar valor"
          ><Pencil size={11} /></button>
        </div>
      </td>
    );
  }

  return (
    <td style={{
      padding: '8px 10px', borderBottom: '1px solid #0f1117',
      borderRight: '1px solid #1a2035', textAlign: 'center',
      background: pago
        ? (highlighted ? 'rgba(74,222,128,0.18)' : 'rgba(74,222,128,0.12)')
        : (highlighted ? 'rgba(246,201,14,0.06)' : ''),
      transition: 'background 0.2s',
      minWidth: 150,
    }}>
      {extra && <div style={{ marginBottom: 6 }}>{extra}</div>}
      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
          <input
            type="number"
            min="0"
            step="0.01"
            value={valorDraft}
            onChange={e => setValorDraft(e.target.value)}
            placeholder="Valor R$"
            style={{ background: '#0f1117', border: '1px solid #1a2035', borderRadius: 5, color: '#e2e8f0', fontSize: 12, padding: '4px 8px', outline: 'none', width: 110 }}
          />
          <input
            type="date"
            value={dataDraft}
            onChange={e => setDataDraft(e.target.value)}
            placeholder="Data pago"
            style={{ background: '#0f1117', border: '1px solid #1a2035', borderRadius: 5, color: '#e2e8f0', fontSize: 12, padding: '4px 6px', outline: 'none', width: 110 }}
          />
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={save}
              disabled={saving}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4ade80', padding: 2, display: 'flex' }}
              title="Salvar"
            ><Check size={15} /></button>
            <button
              onClick={cancelEdit}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', padding: 2, display: 'flex' }}
              title="Cancelar"
            ><X size={14} /></button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: pago ? '#4ade80' : '#cbd5e1' }}>
              {fmt(valor)}
            </div>
            {pago && (
              <div style={{ fontSize: 10, color: '#4ade80', marginTop: 1 }}>
                ✓ {pagoEm}
              </div>
            )}
          </div>
          <button
            onClick={openEdit}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 2, display: 'flex', opacity: 0.6 }}
            title="Editar valor / data"
          ><Pencil size={12} /></button>
        </div>
      )}
    </td>
  );
}

/* ─── Notificação CRT do engenheiro ─── */
function CrtNotificacao({ fin, isChefe, onCrtConfirmado, onCrtCancelado }) {
  const engStatus = fin.eng_status;
  const dataCrt = fin.eng_data_crt_pagamento;
  const crtConfirmado = fin.eng_crt_confirmado;
  const engId = fin.eng_id;

  const [confirmando, setConfirmando] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [dataRetorno, setDataRetorno] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  // Mostra notificação apenas se: status é a_pagar_crt E tem data CRT E não foi cancelado (sem data = cancelado)
  if (engStatus !== 'a_pagar_crt' || !dataCrt) return null;

  const fmtDate = (d) => {
    if (!d) return '—';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  };

  const handleConfirmar = async () => {
    if (!dataRetorno) { alert('Informe a data provável de retorno da CEMIG.'); return; }
    setConfirmando(true);
    try {
      await api.post(`/engenheiro/${engId}/confirmar-crt/`, { data_analise_cemig_retorno: dataRetorno });
      if (onCrtConfirmado) onCrtConfirmado(fin.id);
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao confirmar CRT.');
    } finally { setConfirmando(false); }
  };

  const handleCancelar = async () => {
    setCancelando(true);
    try {
      await api.post(`/engenheiro/${engId}/cancelar-crt/`, {});
      if (onCrtCancelado) onCrtCancelado(fin.id);
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao cancelar.');
    } finally { setCancelando(false); }
  };

  return (
    <div style={{ padding:'6px 8px', borderRadius:6, background:'rgba(251,146,60,0.08)', border:'1px solid rgba(251,146,60,0.3)', display:'flex', flexDirection:'column', gap:4, minWidth:150 }}>
      <span style={{ fontSize:11, color:'#fb923c', fontWeight:700 }}>⚙️ A Pagar CRT</span>
      <span style={{ fontSize:11, color:'#cbd5e1' }}>Prev: {fmtDate(dataCrt)}</span>
      {isChefe && !showConfirm && (
        <div style={{ display:'flex', gap:4, marginTop:2 }}>
          <button onClick={() => setShowConfirm(true)}
            style={{ background:'none', border:'1px solid rgba(74,222,128,0.4)', borderRadius:4, color:'#4ade80', fontSize:10, padding:'2px 7px', cursor:'pointer', fontWeight:700 }}>
            ✓ Confirmar
          </button>
          <button onClick={handleCancelar} disabled={cancelando}
            style={{ background:'none', border:'1px solid rgba(248,113,113,0.3)', borderRadius:4, color:'#f87171', fontSize:10, padding:'2px 7px', cursor:'pointer' }}>
            ✕ Cancelar
          </button>
        </div>
      )}
      {isChefe && showConfirm && (
        <div style={{ display:'flex', flexDirection:'column', gap:4, marginTop:2 }}>
          <span style={{ fontSize:10, color:'#94a3b8' }}>Retorno CEMIG:</span>
          <input type="date" value={dataRetorno} onChange={e => setDataRetorno(e.target.value)}
            style={{ background:'#0f1117', border:'1px solid #1a2035', borderRadius:4, color:'#e2e8f0', fontSize:11, padding:'3px 6px', outline:'none' }} />
          <div style={{ display:'flex', gap:4 }}>
            <button onClick={handleConfirmar} disabled={confirmando || !dataRetorno}
              style={{ background:'none', border:'1px solid rgba(74,222,128,0.4)', borderRadius:4, color:'#4ade80', fontSize:10, padding:'2px 7px', cursor:'pointer', fontWeight:700, opacity: (!dataRetorno || confirmando) ? 0.5 : 1 }}>
              {confirmando ? '...' : '✓ OK'}
            </button>
            <button onClick={() => setShowConfirm(false)}
              style={{ background:'none', border:'none', color:'#475569', fontSize:10, padding:'2px 5px', cursor:'pointer' }}>
              ← Voltar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Célula de comissão (chefe pode editar o % ou valor R$ e a data de pagamento) ─── */
function ComissaoCell({ finId, leadId, pct, valor, faturamento, valorManual, onSaved, desconto, pagaEm }) {
  const [editing, setEditing]     = useState(false);
  // modo: 'pct' ou 'manual'
  const [modo, setModo]           = useState(valorManual != null ? 'manual' : 'pct');
  const [draftPct, setDraftPct]   = useState(String(pct));
  const [draftRs, setDraftRs]     = useState(valorManual != null ? String(valorManual).replace('.', ',') : '');
  const [dataDraft, setDataDraft] = useState(pagaEm ? pagaEm.slice(0, 16) : '');
  const [saving, setSaving]       = useState(false);

  const openEdit = () => {
    setModo(valorManual != null ? 'manual' : 'pct');
    setDraftPct(String(pct));
    setDraftRs(valorManual != null ? String(valorManual).replace('.', ',') : '');
    setDataDraft(pagaEm ? pagaEm.slice(0, 16) : '');
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (modo === 'pct') {
        const novoPct = parseFloat(draftPct.replace(',', '.'));
        if (isNaN(novoPct) || novoPct < 0 || novoPct > 100) return;
        await api.patch(`/leads/${leadId}/`, { comissao_percentual_venda: novoPct, comissao_valor_manual: null });
        await api.patch(`/financeiro/${finId}/`, { comissao_paga_em: dataDraft || null });
        const novoValor = faturamento > 0 ? Math.round(faturamento * novoPct / 100 * 100) / 100 : 0;
        onSaved(finId, novoPct, novoValor, dataDraft || null, null);
      } else {
        const novoRs = parseFloat(draftRs.replace(',', '.'));
        if (isNaN(novoRs) || novoRs < 0) return;
        await api.patch(`/leads/${leadId}/`, { comissao_valor_manual: novoRs });
        await api.patch(`/financeiro/${finId}/`, { comissao_paga_em: dataDraft || null });
        onSaved(finId, pct, novoRs, dataDraft || null, novoRs);
      }
      setEditing(false);
    } catch { /* silencioso */ }
    finally { setSaving(false); }
  };

  if (editing) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
      {/* toggle modo */}
      <div style={{ display:'flex', gap:4 }}>
        <button onClick={() => setModo('pct')}
          style={{ fontSize:10, padding:'2px 8px', borderRadius:10, border:'1px solid', cursor:'pointer',
            background: modo === 'pct' ? '#a78bfa' : 'transparent',
            color: modo === 'pct' ? '#fff' : '#64748b',
            borderColor: modo === 'pct' ? '#a78bfa' : '#1a2035' }}>
          %
        </button>
        <button onClick={() => setModo('manual')}
          style={{ fontSize:10, padding:'2px 8px', borderRadius:10, border:'1px solid', cursor:'pointer',
            background: modo === 'manual' ? '#a78bfa' : 'transparent',
            color: modo === 'manual' ? '#fff' : '#64748b',
            borderColor: modo === 'manual' ? '#a78bfa' : '#1a2035' }}>
          R$
        </button>
      </div>

      {modo === 'pct' ? (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
            <input
              type="number" min="0" max="100" step="0.5"
              value={draftPct}
              onChange={e => setDraftPct(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
              autoFocus
              style={{ width:60, background:'#0f1117', border:'1px solid #a78bfa', borderRadius:5, color:'#e2e8f0', fontSize:12, padding:'3px 6px', outline:'none', textAlign:'center' }}
            />
            <span style={{ fontSize:11, color:'#64748b' }}>%</span>
            <button onClick={save} disabled={saving} style={{ background:'none', border:'none', cursor:'pointer', color:'#4ade80', padding:2 }}><Check size={14}/></button>
            <button onClick={() => setEditing(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'#f87171', padding:2 }}><X size={14}/></button>
          </div>
          {faturamento > 0 && parseFloat(draftPct) > 0 && (
            <div style={{ fontSize:10, color:'#64748b' }}>
              = R$ {(faturamento * parseFloat(draftPct.replace(',','.') || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits:2 })}
            </div>
          )}
        </div>
      ) : (
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          <span style={{ fontSize:11, color:'#64748b' }}>R$</span>
          <input
            type="number" min="0" step="0.01"
            value={draftRs}
            onChange={e => setDraftRs(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
            autoFocus
            style={{ width:90, background:'#0f1117', border:'1px solid #a78bfa', borderRadius:5, color:'#e2e8f0', fontSize:12, padding:'3px 6px', outline:'none', textAlign:'center' }}
          />
          <button onClick={save} disabled={saving} style={{ background:'none', border:'none', cursor:'pointer', color:'#4ade80', padding:2 }}><Check size={14}/></button>
          <button onClick={() => setEditing(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'#f87171', padding:2 }}><X size={14}/></button>
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
        <span style={{ fontSize:10, color:'#64748b' }}>Data pagamento:</span>
        <input
          type="datetime-local"
          value={dataDraft}
          onChange={e => setDataDraft(e.target.value)}
          style={{ background:'#0f1117', border:'1px solid #1a2035', borderRadius:5, color:'#e2e8f0', fontSize:11, padding:'3px 6px', outline:'none', width:160 }}
        />
      </div>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
        <span style={{ fontSize:13, color: pagaEm ? '#4ade80' : (desconto ? '#f59e0b' : '#a78bfa'), fontWeight:700 }}>
          {valor > 0 ? `R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits:2 })}` : '—'}
        </span>
        {desconto && !pagaEm && (
          <span title="Venda com mais de 5% de desconto — edite a comissão para confirmar"
            style={{ background:'#f59e0b', borderRadius:'50%', width:16, height:16, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, color:'#fff', cursor:'default' }}>⚠</span>
        )}
        <button onClick={openEdit}
          style={{ background:'none', border:'none', cursor:'pointer', color:'#64748b', padding:2 }}
          title={desconto ? 'Revisar comissão (desconto > 5%)' : 'Editar comissão / data pagamento'}>
          <Pencil size={11}/>
        </button>
      </div>
      {valorManual != null
        ? <div style={{ fontSize:10, color:'#a78bfa' }}>manual</div>
        : pct > 0 && <div style={{ fontSize:10, color:'#64748b' }}>{pct}%</div>
      }
      {pagaEm
        ? <div style={{ fontSize:10, color:'#4ade80', marginTop:1 }}>✓ paga {new Date(pagaEm).toLocaleDateString('pt-BR')}</div>
        : <div style={{ fontSize:10, color:'#f87171', marginTop:1 }}>⏳ pendente</div>
      }
    </div>
  );
}

/* ─── Tabela de custos ─── */
function TabelaCustos({ financeiros, focusId, onSaved, isChefe, onCrtConfirmado, onCrtCancelado, onComissaoSaved }) {
  if (financeiros.length === 0) {
    return (
      <div style={{ color: '#475569', fontSize: 13, padding: '32px 0', textAlign: 'center' }}>
        Nenhum registro nesta categoria.
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <table style={S.table}>
        <thead>
          <tr style={S.thRow}>
            <th style={S.thLabel}>Custo</th>
            {financeiros.map(f => (
              <th key={f.id} style={{
                ...S.thClient,
                background: f.id === focusId ? 'rgba(246,201,14,0.12)' : '#0b111e',
                borderTop: f.id === focusId ? '2px solid rgba(246,201,14,0.5)' : undefined,
              }}>
                <div>{f.cliente_nome}</div>
                <div style={{ fontSize: 10, color: '#475569', fontWeight: 400, marginTop: 2 }}>{f.cliente_cidade}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {CUSTOS.map(({ key, pagoKey, label }) => (
            <tr key={key}>
              <td style={S.tdLabel}>{label}</td>
              {financeiros.map(f => (
                <Cell
                  key={f.id}
                  finId={f.id}
                  valorKey={key}
                  valor={f[key]}
                  pagoKey={pagoKey}
                  pagoEm={f[pagoKey]}
                  onSaved={onSaved}
                  highlighted={f.id === focusId}
                  extra={key === 'custo_crt' ? (
                    <CrtNotificacao
                      fin={f}
                      isChefe={isChefe}
                      onCrtConfirmado={onCrtConfirmado}
                      onCrtCancelado={onCrtCancelado}
                    />
                  ) : null}
                />
              ))}
            </tr>
          ))}

          {/* Linha de Comissão do Vendedor */}
          <tr style={{ borderTop: '2px solid #1a2035' }}>
            <td style={{ ...S.tdLabel, color: '#a78bfa', fontWeight: 700 }}>Comissão Vendedor</td>
            {financeiros.map(f => {
              const pct         = parseFloat(f.comissao_percentual) || 0;
              const valor       = parseFloat(f.comissao_calculada) || 0;
              const faturamento = parseFloat(f.faturamento_total) || 0;
              const valorManual = f.comissao_valor_manual != null ? parseFloat(f.comissao_valor_manual) : null;
              return (
                <td key={f.id} style={{ ...S.tdTotal, background: f.id === focusId ? 'rgba(167,139,250,0.06)' : '#0b111e', textAlign: 'center' }}>
                  {isChefe
                    ? <ComissaoCell finId={f.id} leadId={f.lead} pct={pct} valor={valor} faturamento={faturamento} valorManual={valorManual} onSaved={onComissaoSaved} desconto={!!f.desconto_revisao_pendente} pagaEm={f.comissao_paga_em || null} />
                    : <div>
                        <div style={{ fontSize: 13, color: '#a78bfa', fontWeight: 700 }}>{valor > 0 ? `R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits:2 })}` : '—'}</div>
                        {valorManual == null && pct > 0 && <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{pct}%</div>}
                      </div>
                  }
                </td>
              );
            })}
          </tr>

          {/* Linha de total */}
          <tr style={{ background: '#0b111e' }}>
            <td style={S.tdTotal}>Total</td>
            {financeiros.map(f => {
              const comissaoValor = parseFloat(f.comissao_calculada) || 0;
              const comissaoPaga  = f.comissao_paga_em ? comissaoValor : 0;
              const total = CUSTOS.reduce((s, { key }) => s + (parseFloat(f[key]) || 0), 0) + comissaoValor;
              const pagos = CUSTOS.reduce((s, { key, pagoKey }) => s + (f[pagoKey] ? (parseFloat(f[key]) || 0) : 0), 0) + comissaoPaga;
              const allPago = total > 0 && pagos >= total;
              return (
                <td key={f.id} style={{
                  ...S.tdTotal,
                  background: allPago ? 'rgba(74,222,128,0.15)' : (f.id === focusId ? 'rgba(246,201,14,0.06)' : '#0b111e'),
                  color: allPago ? '#4ade80' : '#f6c90e',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 13 }}>{fmt(total) || '—'}</div>
                  {total > 0 && pagos > 0 && pagos < total && (
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                      {fmt(pagos)} pagos
                    </div>
                  )}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function CustosComercial() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const params    = new URLSearchParams(location.search);
  const focusId   = params.get('id') ? parseInt(params.get('id')) : null;
  const { isChefe } = useAuth();

  const [financeiros, setFinanceiros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aba, setAba] = useState('pendente');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/financeiro/');
      setFinanceiros(r.data);
    } catch {
      setFinanceiros([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* Atualiza célula localmente sem refazer fetch */
  const handleSaved = (finId, pagoKey, data, valorKey, valorNum) => {
    setFinanceiros(prev => prev.map(f => {
      if (f.id !== finId) return f;
      const updated = { ...f, [pagoKey]: data };
      if (valorKey) updated[valorKey] = valorNum;
      return updated;
    }));
  };

  /* CRT confirmado → engenheiro avança para analise_cemig; some o badge */
  const handleCrtConfirmado = (finId) => {
    setFinanceiros(prev => prev.map(f =>
      f.id === finId ? { ...f, eng_status: 'analise_cemig', eng_crt_confirmado: true } : f
    ));
  };

  /* CRT cancelado → remove data_crt_pagamento; some o badge */
  const handleCrtCancelado = (finId) => {
    setFinanceiros(prev => prev.map(f =>
      f.id === finId ? { ...f, eng_data_crt_pagamento: null } : f
    ));
  };

  const handleComissaoSaved = (finId, novoPct, novoValor, novaData, novoValorManual) => {
    setFinanceiros(prev => prev.map(f =>
      f.id !== finId ? f : {
        ...f,
        comissao_percentual: novoPct,
        comissao_calculada: novoValor,
        comissao_valor_manual: novoValorManual,
        desconto_revisao_pendente: false,
        comissao_paga_em: novaData,
      }
    ));
  };

  const pendentes      = financeiros.filter(f => !isTotalmentePago(f));
  const totalmentePagos = financeiros.filter(f => isTotalmentePago(f));
  const exibidos       = aba === 'pendente' ? pendentes : totalmentePagos;

  if (loading) {
    return (
      <div>
        <div style={S.heading}>Custos Comercial</div>
        <div style={{ color: '#475569', fontSize: 13, marginTop: 24 }}>Carregando...</div>
      </div>
    );
  }

  if (financeiros.length === 0) {
    return (
      <div>
        <div style={S.heading}>Custos Comercial</div>
        <div style={{ color: '#475569', fontSize: 13, marginTop: 24 }}>Nenhum registro financeiro encontrado.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={S.heading}>Custos Comercial</div>
      <div style={S.sub}>
        Acompanhe o pagamento de custos por cliente. Verde = pago. Clique no lápis para registrar a data.
      </div>

      {/* ── Abas ── */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #1a2035' }}>
        {[
          { key: 'pendente',       label: 'Pendente',        count: pendentes.length,       color: '#f6c90e' },
          { key: 'totalmente_pago', label: 'Totalmente pago', count: totalmentePagos.length, color: '#4ade80' },
        ].map(tab => {
          const active = aba === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setAba(tab.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 20px',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: active ? 700 : 500,
                color: active ? tab.color : '#475569',
                borderBottom: active ? `2px solid ${tab.color}` : '2px solid transparent',
                marginBottom: -2,
                transition: 'all 0.15s',
              }}
            >
              {tab.label}
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 20, height: 20, borderRadius: 999,
                background: active ? tab.color : '#1a2035',
                color: active ? '#080c14' : '#475569',
                fontSize: 11, fontWeight: 700, padding: '0 6px',
              }}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {focusId && (
        <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: '#f6c90e', background: 'rgba(246,201,14,0.1)', border: '1px solid rgba(246,201,14,0.3)', borderRadius: 20, padding: '3px 12px' }}>
            ★ Destacando cliente ID #{focusId}
          </span>
          <button
            onClick={() => navigate('/custos-comercial')}
            style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}
          >Mostrar todos</button>
        </div>
      )}

      <TabelaCustos
        financeiros={exibidos}
        focusId={focusId}
        onSaved={handleSaved}
        isChefe={isChefe()}
        onCrtConfirmado={handleCrtConfirmado}
        onCrtCancelado={handleCrtCancelado}
        onComissaoSaved={handleComissaoSaved}
      />
    </div>
  );
}
